import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchExams } from "../actions/examSlice";
import { fetchQuestions,generatePDF } from "../actions/answerExamSlice";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router-dom";
import "../css/MyAccountPage.css";
import Footer from "./Footer";
import Header from "./Header";
import Loading from "./Loading"
import AuthRequired from "./AuthRequired";

const MyAccountPage = () => {
	const auth = useAuth();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { exams, loading, error } = useSelector((state) => state.exams);
	const { answerExamloading } = useSelector((state) => state?.answerExam);
	const token = auth?.user?.id_token;
	const user_id = auth?.user?.profile?.["cognito:username"];

	// State for Exam Report Popup
	const [examReport, setExamReport] = useState(null);
	const [showReportPopup, setShowReportPopup] = useState(false);

	useEffect(() => {
		if (auth?.isLoading) return;
		if (auth?.isAuthenticated && window.location.search.includes("code=")) {
			navigate(window.location.pathname, { replace: true });
		}
		if (token) {
			dispatch(fetchExams({ userId: user_id, token }));
		}
	}, [auth, user_id, dispatch, navigate, token]);



	const handlePDFPrintClick = (exam) => {
		if (exam.isPDFGenerated) {
			window.open(`https://k12questionimages.s3.us-east-1.amazonaws.com/IAAT/exampdfs/${exam.exam_id}.zip`, "_blank");
		} else if(answerExamloading){
			alert("Loading. Please wait.");
		} else {

			const userConfirmation = window.confirm(
				"The request to generate the PDF will be processed shortly. Please check back in a few minutes. Do you want to proceed?"
			);

			if (userConfirmation) {
				dispatch(generatePDF({ user_id, exam_id: exam.exam_id, fetch_type: "GENERATEPDF", token }))
					.then(() => {
						// Navigate back to account page after request
						navigate("/account");
					})
					.catch((error) => {
						navigate("/account");
						console.error("Error generating PDF:", error);
					});
			}
		}
	};


	// Function to fetch report for completed exams
	const fetchExamReport = (exam_id) => {
		dispatch(fetchQuestions({ user_id, exam_id, fetch_type: "ALL", token }))
			.then((action) => {
				if (action.payload.length > 0) {
					generateReport(action.payload);
				}
			});
	};

	// Generate Exam Report
	const generateReport = (questions) => {
		const totalQuestions = questions.length;
		const correctlyAnswered = questions.filter(q => q.student_option === q.answer).length;
		const overallPercentage = ((correctlyAnswered / totalQuestions) * 100).toFixed(2);

		// Group by question_type
		const questionTypeStats = questions.reduce((acc, question) => {
			const { question_type, student_option, answer } = question;
			if (!acc[question_type]) {
				acc[question_type] = { total: 0, correct: 0 };
			}
			acc[question_type].total += 1;
			if (student_option === answer) {
				acc[question_type].correct += 1;
			}
			return acc;
		}, {});

		setExamReport({ totalQuestions, correctlyAnswered, overallPercentage, questionTypeStats });
		setShowReportPopup(true); // Show popup
	};

	const generateReportFromExamDetails = (exam) => {
		if (!exam) return;
	
		const totalQuestions = exam.Total_Questions || 0;
		const correctlyAnswered = exam.Correctly_answered_Questions || 0;
		const overallPercentage = totalQuestions > 0 
			? ((correctlyAnswered / totalQuestions) * 100).toFixed(2) 
			: "0.00";
	
		// Extract question type accuracy details
		const questionTypeStats = {};
		if (exam.exam_type_Accuracy) {
			for (const [key, value] of Object.entries(exam.exam_type_Accuracy)) {
				const total = value["Total Questions"] || 0;
				const correct = value["Correct Answers"] || 0;
				questionTypeStats[key] = {
					total,
					correct,
					percentage: total > 0 ? ((correct / total) * 100).toFixed(2) + "%" : "0.00%"
				};
			}
		}
	
		setExamReport({ totalQuestions, correctlyAnswered, overallPercentage, questionTypeStats });
		setShowReportPopup(true); // Show popup
	};
	

	// Handle Click on Status Icon
	const handleStatusClick = (exam) => {
		if (exam.status === "Completed") {
			generateReportFromExamDetails(exam);
		} else {
			navigate(`/exam/${exam.exam_id}`);
		}
	};

	// Show loading state while auth is processing (including OAuth callback)
	if (auth?.isLoading) {
		return <Loading />;
	}

	// Only show AuthRequired if auth has finished loading and user is not authenticated
	if (!auth?.isAuthenticated) {
		return (<AuthRequired />);
	}

	return (
		<div>
			<Header />
			<div className="account-home">
				{loading && <Loading />}
				{error && <p className="error">{error}</p>}

				{!loading && exams.length === 0 ? (
					<p className="no-exams-message">
						No exams exist. <a href="/createexam" className="create-exam-link">Create an exam</a> to start learning.
					</p>
				) : (

					<div>
						<div className="header-row">
							<h2 className="title">My Exams</h2>
							<button className="create-exam-btn" onClick={() => navigate("/createexam")}>
								Create New Exam
							</button>
						</div>

						<ul className="exam-list">
							{exams.map((exam) => (
								<li key={exam.exam_id} className="exam-card">
									<div className="exam-details">
										<div className="exam-icon-column">
											<img src={exam.exam_type === "IAAT" ? "IAATTest.PNG" : (exam.exam_type === "COGAT" ? "COGATTest.PNG" : "NNATTest.PNG") } alt={exam.exam_type} className="exam-icon" />
										</div>
										<div className="exam-text-column">
											<strong>Exam Type:</strong> {exam.exam_type} <br />
											<strong>Status:</strong> {exam.status} <br />
											<small>Created: {new Date(exam.created_timestamp).toLocaleString()}</small>
										</div>
										<div className="status-icon-column" onClick={() => handlePDFPrintClick(exam)}>
											<img src={exam.isPDFGenerated ? "PDF_Active.PNG" : ( answerExamloading ? "PDF_Loading.PNG" : "PDF.PNG")} alt="Print PDF" className="status-icon" />
										</div>
										<div className="status-icon-column" onClick={() => handleStatusClick(exam)}>
											<img src={exam.status === "New" ? "start.PNG" : exam.status === "In Process" ? "resume.PNG" : "view.PNG"} alt={exam.status} className="status-icon" />
										</div>
									</div>
								</li>
							))}
						</ul>
					</div>
				)}

				{/* Exam Report Popup */}
				{showReportPopup && examReport && (
					<div className="report-popup">
						<div className="report-content">
						<button className="close-popup" onClick={() => setShowReportPopup(false)}>×</button>
						<h2 className="report-title">Exam Report</h2>

						{/* Total Questions and Accuracy */}
						<div className="report-summary">
							<div className="summary-box total">
							<h3>Total Questions</h3>
							<p>{examReport.totalQuestions}</p>
							</div>
							<div className="summary-box accuracy">
							<h3>Accuracy</h3>
							<p>{examReport.overallPercentage}%</p>
							</div>
						</div>

						{/* Performance by Question Type Table */}
						<div className="report-table-container">
							<table className="report-table">
							<thead>
								<tr>
								<th>Question Type</th>
								<th>Correct Answers</th>
								<th>Total Questions</th>
								<th>Accuracy (%)</th>
								</tr>
							</thead>
							<tbody>
								{Object.entries(examReport.questionTypeStats).map(([type, stats]) => (
								<tr key={type}>
									<td>{type}</td>
									<td>{stats.correct}</td>
									<td>{stats.total}</td>
									<td>{((stats.correct / stats.total) * 100).toFixed(2)}%</td>
								</tr>
								))}
							</tbody>
							</table>
						</div>
						</div>
					</div>
					)}
			</div>
			<Footer />
		</div>
	);
};

export default MyAccountPage;
